import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Users, Plus, Edit, Trash2, Copy, Search, UserCheck, Bot, User, Rocket } from 'lucide-react'
import { useQuery, useQueryClient } from 'react-query'
import { teamService } from '../services/api'
import type { TeamTemplate, TeamRole } from '../types/team'

export function Teams() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedTeam, setSelectedTeam] = useState<TeamTemplate | null>(null)
  const [showRoleForm, setShowRoleForm] = useState(false)
  const [editingRole, setEditingRole] = useState<TeamRole | null>(null)
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    isUnique: false,
    agentType: 'ai' as 'ai' | 'human' | 'both'
  })
  const [showCreateTeamForm, setShowCreateTeamForm] = useState(false)
  const [newTeam, setNewTeam] = useState({
    name: '',
    description: ''
  })

  const { data: teams, isLoading, error } = useQuery(
    'teamTemplates',
    teamService.getAllTeamTemplates
  )

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleDeleteTeam = async (teamId: string, teamName: string) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le modèle d'équipe "${teamName}" ?\n\nCette action est irréversible.`)) {
      try {
        await teamService.deleteTeamTemplate(teamId)
        queryClient.invalidateQueries('teamTemplates')
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        alert('Erreur lors de la suppression du modèle d\'équipe')
      }
    }
  }

  const handleDuplicateTeam = async (teamId: string) => {
    try {
      await teamService.duplicateTeamTemplate(teamId)
      queryClient.invalidateQueries('teamTemplates')
    } catch (error) {
      console.error('Erreur lors de la duplication:', error)
      alert('Erreur lors de la duplication du modèle d\'équipe')
    }
  }

  const handleAddRole = async () => {
    if (!selectedTeam || !newRole.name.trim()) {
      alert('Veuillez remplir le nom du rôle')
      return
    }

    try {
      if (editingRole) {
        await teamService.updateTeamRole(selectedTeam.id, editingRole.id, newRole)
      } else {
        await teamService.addTeamRole(selectedTeam.id, newRole)
      }
      queryClient.invalidateQueries('teamTemplates')
      setShowRoleForm(false)
      setEditingRole(null)
      setNewRole({ name: '', description: '', isUnique: false, agentType: 'ai' })
    } catch (error) {
      console.error('Erreur lors de l\'ajout/modification du rôle:', error)
      alert('Erreur lors de l\'ajout/modification du rôle')
    }
  }

  const handleDeleteRole = async (teamId: string, roleId: string, roleName: string) => {
    if (confirm(`Êtes-vous sûr de vouloir supprimer le rôle "${roleName}" ?`)) {
      try {
        await teamService.deleteTeamRole(teamId, roleId)
        queryClient.invalidateQueries('teamTemplates')
      } catch (error) {
        console.error('Erreur lors de la suppression:', error)
        alert('Erreur lors de la suppression du rôle')
      }
    }
  }

  const handleCreateTeam = async () => {
    if (!newTeam.name.trim()) {
      alert('Veuillez remplir le nom du modèle d\'équipe')
      return
    }

    try {
      await teamService.createTeamTemplate(newTeam)
      queryClient.invalidateQueries('teamTemplates')
      setShowCreateTeamForm(false)
      setNewTeam({ name: '', description: '' })
    } catch (error) {
      console.error('Erreur lors de la création:', error)
      alert('Erreur lors de la création du modèle d\'équipe')
    }
  }

  const handleEditRole = (role: TeamRole) => {
    setEditingRole(role)
    setNewRole({
      name: role.name,
      description: role.description || '',
      isUnique: role.isUnique,
      agentType: role.agentType
    })
    setShowRoleForm(true)
  }

  const filteredTeams = teams?.filter(team => 
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || []

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 spinner"></div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Erreur lors du chargement des modèles d'équipes</p>
      </div>
    )
  }

  return (
    <div className="page-content relative min-h-screen">
      {/* Image de robot en arrière-plan */}
      <div className="background-image">
        <img src="/logo.jpg" alt="Robot background" />
      </div>
      
      {/* Contenu au premier plan */}
      <div className="content-wrapper">
        <div className="space-y-6">
          {/* En-tête de page */}
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-extrabold mb-2 tracking-tight tab_title">Modèles d'équipes</h1>
            <button
              onClick={() => {
                setShowCreateTeamForm(true)
                setNewTeam({ name: '', description: '' })
              }}
              className="button button--primary"
            >
              <Plus className="w-5 h-5" />
              <span>Nouveau modèle d'équipe</span>
            </button>
          </div>

          {/* Formulaire de création de modèle d'équipe */}
          {showCreateTeamForm && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-primary">Créer un modèle d'équipe</h2>
                <button
                  onClick={() => {
                    setShowCreateTeamForm(false)
                    setNewTeam({ name: '', description: '' })
                  }}
                  className="button button--secondary"
                >
                  Annuler
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Nom du modèle *</label>
                  <input
                    type="text"
                    value={newTeam.name}
                    onChange={(e) => setNewTeam({ ...newTeam, name: e.target.value })}
                    className="form-input w-full"
                    placeholder="ex: Équipe de développement"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Description</label>
                  <textarea
                    value={newTeam.description}
                    onChange={(e) => setNewTeam({ ...newTeam, description: e.target.value })}
                    className="form-input w-full"
                    rows={3}
                    placeholder="Description du modèle d'équipe..."
                  />
                </div>
                <div className="flex justify-end space-x-2">
                  <button
                    onClick={() => {
                      setShowCreateTeamForm(false)
                      setNewTeam({ name: '', description: '' })
                    }}
                    className="button button--secondary"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleCreateTeam}
                    className="button button--primary"
                  >
                    Créer
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Barre de recherche */}
          <div className="card">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 icon-tertiary" />
              <input
                type="text"
                placeholder="Rechercher un modèle d'équipe..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="form-input w-full pl-10"
              />
            </div>
          </div>

          {/* Table des modèles d'équipes */}
          <div className="card overflow-hidden">
            {filteredTeams.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto mb-4 icon-light" />
                <h3 className="text-lg font-medium mb-2 text-primary">Aucun modèle d'équipe trouvé</h3>
                <p className="mb-4 text-secondary">
                  {searchTerm 
                    ? 'Aucun modèle ne correspond à votre recherche.' 
                    : 'Commencez par créer votre premier modèle d\'équipe.'}
                </p>
                {!searchTerm && (
                  <button
                    onClick={() => {
                      setShowCreateTeamForm(true)
                      setNewTeam({ name: '', description: '' })
                    }}
                    className="button button--primary"
                  >
                    <Plus className="w-5 h-5" />
                    <span>Créer un modèle d'équipe</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="table-header">
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Nom
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Rôles
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider table-header-cell">
                        Date de création
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider table-header-cell">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredTeams.map((team, index) => (
                      <tr 
                        key={team.id}
                        className={`table-row table-hover-row ${index > 0 ? '' : ''}`}
                        onClick={() => setSelectedTeam(team)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="w-5 h-5 mr-3 text-robot-blue" />
                            <div>
                              <div className="text-sm font-medium text-primary">
                                {team.name}
                              </div>
                              {team.description && (
                                <div className="text-sm text-secondary">
                                  {team.description.length > 50 
                                    ? `${team.description.substring(0, 50)}...` 
                                    : team.description}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-wrap gap-2">
                            {team.roles.map(role => (
                              <span
                                key={role.id}
                                className="inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold bg-robot-blue-light text-robot-blue"
                              >
                                {role.name}
                                {role.isUnique && <span className="ml-1">🔒</span>}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-primary">
                            {formatDate(team.createdAt)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setSelectedTeam(team)
                                setShowRoleForm(true)
                                setEditingRole(null)
                                setNewRole({ name: '', description: '', isUnique: false, agentType: 'ai' })
                              }}
                              className="action-button action-button--primary"
                              title="Ajouter un rôle"
                            >
                              <Plus className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDuplicateTeam(team.id)
                              }}
                              className="action-button action-button--primary"
                              title="Dupliquer"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleDeleteTeam(team.id, team.name)
                              }}
                              className="action-button action-button--danger"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Panel de gestion des rôles */}
          {selectedTeam && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-primary">
                  Rôles de l'équipe "{selectedTeam.name}"
                </h2>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={async () => {
                      try {
                        const instanceName = prompt('Nom de l\'instance d\'équipe (optionnel):')
                        await teamService.createTeamInstance(selectedTeam.id, instanceName || undefined)
                        queryClient.invalidateQueries('teamInstances')
                        alert('Instance d\'équipe créée avec succès !')
                      } catch (error) {
                        console.error('Erreur lors de la création de l\'instance:', error)
                        alert('Erreur lors de la création de l\'instance d\'équipe')
                      }
                    }}
                    className="button button--primary"
                    title="Créer une instance de cette équipe"
                  >
                    <Rocket className="w-4 h-4 mr-2" />
                    Déployer une instance
                  </button>
                  <button
                    onClick={() => {
                      setSelectedTeam(null)
                      setShowRoleForm(false)
                      setEditingRole(null)
                    }}
                    className="button button--secondary"
                  >
                    Fermer
                  </button>
                </div>
              </div>

              {showRoleForm && (
                <div className="mb-6 p-4 bg-secondary rounded-lg">
                  <h3 className="text-lg font-medium mb-4">
                    {editingRole ? 'Modifier le rôle' : 'Ajouter un rôle'}
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Nom du rôle *</label>
                      <input
                        type="text"
                        value={newRole.name}
                        onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                        className="form-input w-full"
                        placeholder="ex: Manager, Assistant, Analyste..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Description</label>
                      <textarea
                        value={newRole.description}
                        onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                        className="form-input w-full"
                        rows={2}
                        placeholder="Description du rôle..."
                      />
                    </div>
                    <div className="flex items-center space-x-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={newRole.isUnique}
                          onChange={(e) => setNewRole({ ...newRole, isUnique: e.target.checked })}
                          className="mr-2"
                        />
                        <span className="text-sm">Rôle unique (une seule instance)</span>
                      </label>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Type d'agent</label>
                      <select
                        value={newRole.agentType}
                        onChange={(e) => setNewRole({ ...newRole, agentType: e.target.value as 'ai' | 'human' | 'both' })}
                        className="form-input w-full"
                      >
                        <option value="ai">Agent IA uniquement</option>
                        <option value="human">Agent humain uniquement</option>
                        <option value="both">Agent IA ou humain</option>
                      </select>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={handleAddRole}
                        className="button button--primary"
                      >
                        {editingRole ? 'Modifier' : 'Ajouter'}
                      </button>
                      <button
                        onClick={() => {
                          setShowRoleForm(false)
                          setEditingRole(null)
                          setNewRole({ name: '', description: '', isUnique: false, agentType: 'ai' })
                        }}
                        className="button button--secondary"
                      >
                        Annuler
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {selectedTeam.roles.length === 0 ? (
                  <p className="text-sm text-secondary text-center py-4">
                    Aucun rôle défini. Cliquez sur "Ajouter un rôle" pour commencer.
                  </p>
                ) : (
                  selectedTeam.roles.map(role => (
                    <div
                      key={role.id}
                      className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                    >
                      <div className="flex items-center space-x-3">
                        {role.agentType === 'ai' && <Bot className="w-5 h-5 text-robot-blue" />}
                        {role.agentType === 'human' && <User className="w-5 h-5 text-robot-blue" />}
                        {role.agentType === 'both' && <UserCheck className="w-5 h-5 text-robot-blue" />}
                        <div>
                          <div className="font-medium text-primary">
                            {role.name}
                            {role.isUnique && <span className="ml-2 text-xs">🔒 Unique</span>}
                          </div>
                          {role.description && (
                            <div className="text-sm text-secondary">
                              {role.description}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditRole(role)}
                          className="action-button action-button--primary"
                          title="Modifier"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteRole(selectedTeam.id, role.id, role.name)}
                          className="action-button action-button--danger"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
